import { useLocation } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { CustomNotification } from 'common/components';

import * as productServices from 'services/product';
import { forwardTo } from 'utils/common/route';

export const productFullViewKeys = {
  all: ['product-full-view'],
  itemData: (productId) => [...productFullViewKeys.all, parseInt(productId)],
};

export const productOriginFullViewKeys = {
  all: ['pim/overlays', 'get-product-original-details-full-view'],
  itemData: (productId) => [
    ...productOriginFullViewKeys.all,
    { params: { productItemId: parseInt(productId) } },
  ],
};

const defaultProductFull = {};

export const useGetProductFullView = ({ productId, ...otherOptions } = {}) => {
  const queryClient = useQueryClient();
  const location = useLocation();
  const isMemberProductView =
    location.pathname.includes('/member-management-company') ||
    location.pathname.includes('/company');

  const queries = useQuery({
    queryKey: productFullViewKeys.itemData(productId),
    queryFn: async () => {
      const response = await productServices.getProductFullView({
        productItemId: productId,
      });

      if (response?.isSuccess) {
        return response?.data;
      }

      if (!isMemberProductView) {
        CustomNotification.error('Something went wrong');
        forwardTo('/products');

        return defaultProductFull;
      }
    },
    enabled: Boolean(productId),
    meta: {
      onError: () => {
        setTimeout(() => {
          forwardTo('/products');
        }, 100);

        return 'Something went wrong';
      },
    },
    retry: false,
    ...otherOptions,
  });

  const handleRefetchProductFullView = () => {
    queryClient.invalidateQueries({
      queryKey: productFullViewKeys.itemData(productId),
    });
  };

  const invalidateAllProductFull = () => {
    queryClient.invalidateQueries({
      queryKey: productFullViewKeys.all,
    });
  };

  return {
    productFull: queries?.data ?? defaultProductFull,
    handleRefetchProductFullView,
    invalidateAllProductFull,
    ...queries,
  };
};

export const useGetProductOriginFullViewForProductOverlay = ({
  productOverlayId,
  ...otherOptions
} = {}) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: productOriginFullViewKeys.itemData(productOverlayId),
    staleTime: 30 * 1000,
    enabled: Boolean(productOverlayId),
    ...otherOptions,
  });

  const invalidAllProductOriginFull = () => {
    queryClient.invalidateQueries({
      queryKey: productOriginFullViewKeys.all,
    });
  };

  return {
    ...query,
    invalidAllProductOriginFull,
  };
};
